import React, { createContext, useContext, ReactNode, useState } from "react";
import {
  SheetType,
  SheetComponents,
  SheetDataMap,
  SheetContent,
  CommunicationsSheetData,
  MemberSheetDataType,
  ClassCheckinSheetData,
} from "../DashboardSheetContent/SheetContent";
import { CelebrationsMetadata } from "../../../apis/metricsApi";

interface DashboardContextType {
  isSheetOpen: boolean;
  sheetContent: SheetContent | null;
  openSheet: (
    type: SheetType,
    title: string,
    data: SheetDataMap[SheetType],
    reportUrl?: string,
  ) => void;
  closeSheet: () => void;
  uiPreferences: {
    isProgressBarHidden: boolean;
    isQuickActionsHidden: boolean;
  };
  updateUIPreference: (
    key: keyof DashboardContextType["uiPreferences"],
    value: boolean,
  ) => void;
  localCelebrationsMetadataCache: CelebrationsMetadata[];
  setLocalCelebrationsMetadataCache: React.Dispatch<
    React.SetStateAction<CelebrationsMetadata[]>
  >;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined,
);

export function DashboardProvider({ children }: { children: ReactNode }) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [sheetContent, setSheetContent] = useState<SheetContent | null>(null);
  const [localCelebrationsMetadataCache, setLocalCelebrationsMetadataCache] =
    useState<CelebrationsMetadata[]>([]);

  const [uiPreferences, setUIPreferences] = useState(() => ({
    isProgressBarHidden:
      localStorage.getItem("dashboard.isProgressBarHidden") === "true",
    isQuickActionsHidden:
      localStorage.getItem("dashboard.isQuickActionsHidden") === "true",
  }));

  const updateUIPreference = (
    key: keyof typeof uiPreferences,
    value: boolean,
  ) => {
    setUIPreferences((prev) => {
      const newPreferences = { ...prev, [key]: value };
      localStorage.setItem(`dashboard.${key}`, value.toString());
      return newPreferences;
    });
  };

  const openSheet = (
    type: SheetType,
    title: string,
    data: SheetDataMap[SheetType],
    reportUrl?: string,
  ) => {
    switch (type) {
      case "members":
        setSheetContent({
          title,
          children: (
            <SheetComponents.members
              data={data as MemberSheetDataType}
              title={title}
              reportUrl={reportUrl}
            />
          ),
        });
        break;
      case "communications":
        setSheetContent({
          title,
          children: (
            <SheetComponents.communications
              data={data as CommunicationsSheetData}
              title={title}
              reportUrl={reportUrl}
            />
          ),
        });
        break;
      case "classCheckin":
        setSheetContent({
          title,
          children: (
            <SheetComponents.classCheckin
              data={data as ClassCheckinSheetData}
              title={title}
              reportUrl={reportUrl}
            />
          ),
        });
        break;
    }
    setIsSheetOpen(true);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
    setSheetContent(null);
  };

  return (
    <DashboardContext.Provider
      value={{
        isSheetOpen,
        sheetContent,
        openSheet,
        closeSheet,
        uiPreferences,
        updateUIPreference,
        localCelebrationsMetadataCache,
        setLocalCelebrationsMetadataCache,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
};
